<script setup>
import { computed } from 'vue';

import { useModKey } from '../composables/useModKey';
import { HrBtn, HrTooltip } from '../primitives';

/**
 * Dense icon-or-icon+label button for toolbars, editors, and compact action bars.
 *
 * @status recommended
 * @group actions
 * @useWhen Toolbar actions, text-editor formatting controls, or dense icon-row actions
 * @related HrToolbarDropdown
 * @a11y Icon-only mode: supply aria-label via attrs. Tooltip visible on hover and focus.
 */
defineOptions({ name: 'HrToolbarBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

const props = defineProps({
  /**
   * Button text label. When null the button renders as icon-only.
   * @replacesSlot default
   */
  label: {
    type: String,
    default: null,
  },
  /** Icon name. When used without a label the button renders as a fixed square. */
  icon: {
    type: String,
    default: null,
  },
  /** Switches the color role to error. */
  destructive: {
    type: Boolean,
    default: false,
  },
  /** Renders a pill-shaped skeleton placeholder instead of the button. */
  pending: {
    type: Boolean,
    default: false,
  },
  /** Vue Router route location. Renders the button as a router-link. */
  to: {
    type: [String, Object],
    default: null,
  },
  /** Replace the current history entry instead of pushing a new one. */
  replace: {
    type: Boolean,
    default: false,
  },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: {
    type: String,
    default: null,
  },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: {
    type: String,
    default: null,
  },
  /**
   * Marks the button as pressed/selected — e.g. when a formatting mark is active in an editor.
   * Renders a tonal fill (M3 secondary-container) in the new theme; falls back to primary color
   * in the legacy theme.
   */
  active: {
    type: Boolean,
    default: false,
  },
  /**
   * Text for the tooltip shown on hover. When null no tooltip is rendered.
   */
  tooltip: {
    type: String,
    default: null,
  },
  /**
   * Keyboard shortcut label shown below the tooltip text, e.g. "CTRL + B".
   * Ignored when tooltip is null.
   */
  shortcut: {
    type: String,
    default: null,
  },
});

// Only forward props that HrBtn understands — active/tooltip/shortcut must not reach q-btn.
const btnProps = computed(() => ({
  label: props.label,
  icon: props.icon,
  destructive: props.destructive,
  pending: props.pending,
  to: props.to,
  replace: props.replace,
  href: props.href,
  target: props.target,
}));

const { resolveShortcut } = useModKey();
const resolvedShortcut = computed(() => resolveShortcut(props.shortcut));

// Icon-only mode: fixed square container. Text+icon or label-only keeps HrBtn's default sizing.
const classes = computed(() => [
  'hr-toolbar-btn',
  !props.label && props.icon ? 'hr-toolbar-btn--icon' : null,
  props.active ? 'hr-toolbar-btn--active' : null,
]);

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = computed(() => ({
  flat: true,
  'no-caps': true,
  'no-wrap': true,
  size: 'sm',
  padding: '4px 8px',
  color: props.active ? 'primary' : 'dark',
}));
</script>
<template>
  <hr-btn
    variant="text"
    size="xs"
    no-wrap
    :class="classes"
    :legacy-props="legacyProps"
    v-bind="btnProps"
    @click="emit('click', $event)"
  >
    <hr-tooltip
      v-if="tooltip"
      :offset="[0, 8]"
      :delay="1000"
    >
      {{ tooltip }}
      <p
        v-if="resolvedShortcut"
        class="no-margin hr-toolbar-btn__shortcut"
      >
        ({{ resolvedShortcut }})
      </p>
    </hr-tooltip>
    <!-- @slot Button label / content. -->
    <slot />
  </hr-btn>
</template>
<style lang="scss">
.hr-toolbar-btn__shortcut {
  font-size: 0.6rem;
}

/* ─── New theme (hr-btn class present) ─── */

/* All toolbar buttons: 32px height, rounded-square corners (overrides HrBtn's pill default) */
.hr-btn.hr-toolbar-btn {
  min-height: 32px;
  padding-inline: 6px;
  border-radius: var(--app-shape-corner-small);
}

/* Icon-only: additionally enforce square width, zero padding, and 20px icon */
.hr-btn.hr-toolbar-btn--icon {
  min-width: 32px;
  padding: 0;

  :deep(.q-icon) { font-size: 20px; }
}

/* Selected: paint the existing container with tonal fill — no size change */
.hr-btn.hr-toolbar-btn--icon.hr-toolbar-btn--active {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);

  &::before { background: var(--app-on-secondary-container); }
}

/*
 * Disabled: transparent background, dimmed icon only.
 * Overrides HrBtn's default grey-filled disabled state so the button stays the
 * same shape as its enabled neighbors — just dimmed.
 */
.hr-btn.hr-toolbar-btn--icon.disabled {
  background: transparent;
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

/* ─── Legacy theme fallback (hr-btn class absent) ─── */
.hr-toolbar-btn--active {
  color: var(--app-primary, #{$primary});
}

/* Icon-to-label gap — Quasar defaults to 12px; cap at 8px for toolbar density */
.hr-toolbar-btn .on-left {
  margin-right: 8px;
}
</style>
